/* LoopCell motion layer: sections and cards fade up as they scroll into view (staggered).
 * Content already on screen at load is never hidden (no flash). Off for prefers-reduced-motion.
 * Loaded only when Theme settings → Animations → "Reveal sections on scroll" is on. */
(() => {
  if (!('IntersectionObserver' in window)) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce.matches) return;
  const root = document.documentElement;

  // Sections that hold sticky/fixed UI (product purchase flow, Sell flow, cart) are never moved.
  const SKIP = 'sf8-device-journey, sf8-buyback, cart-items, .sf8-sticky-steps, [data-sf-no-reveal]';
  // Children that come in one after another inside a revealed section.
  const GROUPS = [
    '.sf-product-grid > *',
    '.live-category-grid > *',
    '.sf-service-strip > a',
    '.about-values > *',
    '.sf-page-articles > *',
    '.cl-subcategories > *',
    '.sf-footer-grid > *',
  ].join(',');
  const STEP = 70; // ms between items
  const MAX_STAGGER = 6;

  const inView = (el) => {
    const r = el.getBoundingClientRect();
    return r.top < window.innerHeight * 0.92 && r.bottom > 0;
  };

  const done = (el) => {
    el.classList.remove('sf-reveal', 'sf-in');
    el.style.removeProperty('--sf-delay');
  };

  const show = (el) => {
    el.classList.add('sf-in');
    let finished = false;
    const end = (event) => {
      if (event && (event.target !== el || event.propertyName !== 'transform')) return;
      if (finished) return;
      finished = true;
      el.removeEventListener('transitionend', end);
      done(el);
    };
    el.addEventListener('transitionend', end);
    setTimeout(end, 1600); // safety net
  };

  const pending = new Set();
  const reveal = (target) => {
    if (!pending.delete(target)) return;
    observer.unobserve(target);
    show(target);
    target.querySelectorAll('.sf-reveal').forEach(show);
  };
  const observer = new IntersectionObserver(
    (entries) => entries.forEach((entry) => entry.isIntersecting && reveal(entry.target)),
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );
  // A fast jump (End key, anchor link, quick fling) can skip past a section: show everything at or above the fold.
  let ticking = false;
  const sweep = () => {
    ticking = false;
    pending.forEach((target) => target.getBoundingClientRect().top < window.innerHeight && reveal(target));
  };
  window.addEventListener('scroll', () => { if (!ticking && pending.size) { ticking = true; requestAnimationFrame(sweep); } }, { passive: true });
  // The browser may restore the scroll position after a reload or back/forward without a scroll event.
  ['load', 'pageshow'].forEach((type) => window.addEventListener(type, () => { sweep(); setTimeout(sweep, 350); }));

  const prepare = (scope) => {
    const sections = scope.querySelectorAll('main .shopify-section, .shopify-section-group-footer-group, .shopify-section[id*="footer"]');
    sections.forEach((section, index) => {
      if (section.dataset.sfMotion) return;
      section.dataset.sfMotion = '1';
      if (section.closest('main') && section === section.parentElement.firstElementChild && index === 0) return; // hero handles itself
      if (section.querySelector(SKIP)) return;
      const box = section.querySelector(':scope > :not(link, script, style, template)');
      if (!box || inView(box) || box.getBoundingClientRect().bottom <= 0) return; // never hide what is on or above the screen
      box.classList.add('sf-reveal');
      let i = 0;
      box.querySelectorAll(GROUPS).forEach((child) => {
        child.classList.add('sf-reveal');
        child.style.setProperty('--sf-delay', Math.min(i++, MAX_STAGGER) * STEP + 120 + 'ms');
      });
      pending.add(box);
      observer.observe(box);
    });
  };

  // Hero: the product image floats with the mouse (as in the preview); inspection cards tilt slightly.
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType !== 'mouse' || reduce.matches) return;
        const hero = event.target.closest && event.target.closest('sf-slideshow');
        if (hero) {
          const r = hero.getBoundingClientRect();
          const x = (event.clientX - r.left) / r.width - 0.5;
          const y = (event.clientY - r.top) / r.height - 0.5;
          hero.style.setProperty('--float-x', (x * 14).toFixed(1) + 'px');
          hero.style.setProperty('--float-y', (y * 10).toFixed(1) + 'px');
          hero.style.setProperty('--float-angle', (x * 6).toFixed(1) + 'deg');
        }
        const card = event.target.closest && event.target.closest('.phone-inspection-check');
        if (card) {
          const r = card.getBoundingClientRect();
          const x = (event.clientX - r.left) / r.width - 0.5;
          const y = (event.clientY - r.top) / r.height - 0.5;
          card.style.transform = `perspective(750px) rotateX(${(-y * 4).toFixed(2)}deg) rotateY(${(x * 4).toFixed(2)}deg) translateY(-2px)`;
        }
      },
      { passive: true }
    );
    document.addEventListener(
      'pointerout',
      (event) => {
        const hero = event.target.closest && event.target.closest('sf-slideshow');
        if (hero && !hero.contains(event.relatedTarget)) ['--float-x', '--float-y', '--float-angle'].forEach((k) => hero.style.removeProperty(k));
        const card = event.target.closest && event.target.closest('.phone-inspection-check');
        if (card && !card.contains(event.relatedTarget)) card.style.transform = '';
      },
      { passive: true }
    );
  }

  root.classList.add('sf-motion');
  const start = () => prepare(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  // Theme editor: newly added or reloaded sections.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('.sf-reveal').forEach(done);
    prepare(event.target.parentElement || document);
  });
  reduce.addEventListener?.('change', () => document.querySelectorAll('.sf-reveal').forEach(done));
})();
